import { IStudentAssignmentsGrid } from '@app/features/teacher/grading/unit-grading/grade-list-presentation/grade-list-smart.component';
import { GradeLevel } from './grade-level';
import { GradeUnitDayDTO, IGradeUnitDayDTO } from './grade-unit/grade-unit-day';
import {
  GradingPeriodDTO,
  GradingType,
  IGradingPeriodDTO,
} from './grading-period';
import { INoteDTO } from './note';
import { ISchoolDTO, SchoolDTO } from './school';
import { ScoreDTO } from './score';
import { IStudentDTO, StudentDTO } from './student';
import { ITeacherDTO, TeacherDTO } from './teacher';
import { IInviteCode } from './teacher-classroom';
import { GradeUnitDTO } from './grade-unit/grade-unit';
export enum StudentAssignmentStatus {
  UNASSIGNED = 'Unassigned',
  OVERDUE = 'Overdue',
  WRAP_UP = 'Wrap Up',
  COMPLETED = 'Completed',
  STARTED = 'Started',
  ASSIGNED = 'Assigned',
  NOT_STARTED = 'Not Started',
}

export interface ITeacherClassroomDTO {
  classroomId: number;
  classroom: IClassroomDTO;
  teacherId: string;
  teacher: ITeacherDTO;
  schoolId: number;
  school: ISchoolDTO;
}

export class TeacherClassroomDTO implements ITeacherClassroomDTO {
  classroomId: number = 0;
  classroom: IClassroomDTO = new ClassroomDTO();
  teacherId: string = '';
  teacher: ITeacherDTO = new TeacherDTO();
  schoolId: number = 0;
  school: ISchoolDTO = new SchoolDTO();
  constructor(configOverride?: Partial<ITeacherClassroomDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IClassroomInvite {
  createdAt: Date;
  classroomId: string;
  classroom: IClassroomDTO;
  inviteCode: IInviteCode;
}

export class ClassroomInvite implements IClassroomInvite {
  createdAt: Date = null;
  classroomId: string = '';
  classroom: IClassroomDTO = new ClassroomDTO();
  inviteCode: IInviteCode = null;
  constructor(configOverride?: Partial<IClassroomInvite>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IClassroomDTO {
  id: string;
  gradeLevel: GradeLevel;
  className: string;
  classIcon?: string;
  classroomTeachers: ITeacherClassroomDTO[];
  classroomStudents: IClassroomStudentDTO[];
  classroomInvites: IClassroomInvite[];
}

export class ClassroomDTO implements IClassroomDTO {
  id: string = '';
  gradeLevel: GradeLevel;
  className: string = '';
  classIcon?: string = '';
  classroomTeachers: ITeacherClassroomDTO[] = [];
  classroomStudents: IClassroomStudentDTO[] = [];
  classroomInvites: IClassroomInvite[] = [];
  constructor(configOverride?: Partial<IClassroomDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IClassroomStudentDTO {
  classroomId: string;
  classroom: IClassroomDTO;
  studentId: string;
  student: IStudentDTO;
}

export class ClassroomStudentDTO implements IClassroomStudentDTO {
  classroomId: string = '';
  classroom: IClassroomDTO = new ClassroomDTO();
  studentId: string = '';
  student: IStudentDTO = new StudentDTO();
  constructor(configOverride?: Partial<IClassroomStudentDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IClassroomAssignmentDTO {
  id: number;
  dueDate?: Date;
  status: StudentAssignmentStatus;
  classroomId: string;
  gradingPeriodId: number;
  gradeUnitDayId: number;
  gradingPeriod: IGradingPeriodDTO;
  gradeUnitDay: IGradeUnitDayDTO;
  classroom: IClassroomDTO;
  baseScore: number;
  fullCreditAssigned: boolean;
}

export class ClassroomAssignmentDTO implements IClassroomAssignmentDTO {
  id: number = 0;
  dueDate?: Date = null;
  status: StudentAssignmentStatus = StudentAssignmentStatus.ASSIGNED;
  classroomId: string = '';
  gradingPeriodId: number = 0;
  gradeUnitDayId: number = 0;
  gradingPeriod: IGradingPeriodDTO = new GradingPeriodDTO();
  gradeUnitDay: IGradeUnitDayDTO = new GradeUnitDayDTO();
  classroom: IClassroomDTO = new ClassroomDTO();
  baseScore: number = 0;
  fullCreditAssigned: boolean = false;

  constructor(configOverride?: Partial<IStudentAssignmentDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IStudentAssignmentDTO {
  id: number;
  completedAt?: Date;
  assignmentStatus: StudentAssignmentStatus;
  classroomAssignmentId: number;
  studentId: string;
  classroomAssignment: IClassroomAssignmentDTO;
  student: IStudentDTO;
  actualScore?: number;
  finalScore?: number;
  totalPossibleScore?: number;
  notes?: INoteDTO[];
  scores?: ScoreDTO[];
}

export class StudentAssignmentDTO implements IStudentAssignmentDTO {
  id: number;
  completedAt?: Date = null;
  assignmentStatus: StudentAssignmentStatus = StudentAssignmentStatus.STARTED;
  classroomAssignmentId: number = 0;
  studentId: string = '';
  classroomAssignment: IClassroomAssignmentDTO = new ClassroomAssignmentDTO();
  gradeUnit: GradeUnitDTO;
  gradeUnitDay: GradeUnitDayDTO;
  student: IStudentDTO = new StudentDTO();
  finalScore?: number = null;
  actualScore?: number = null;
  totalPossibleScore?: number;
  notes?: INoteDTO[] = [];
  scores?: ScoreDTO[] = [];

  constructor(configOverride?: Partial<IStudentAssignmentDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface IStudentAssignmentRequest {
  studentId?: string;
  assignmentStatus?: StudentAssignmentStatus;
  completedAt?: Date;
  classroomAssignmentId?: number;
  finalScore?: number;
  actualScore?: number;
  gradeUnitId: number;
  gradeUnitDayId: number;
}

export class StudentAssignmentRequest implements IStudentAssignmentRequest {
  studentId?: string = '';
  assignmentStatus?: StudentAssignmentStatus = StudentAssignmentStatus.ASSIGNED;
  completedAt?: Date = null;
  classroomAssignmentId?: number = 0;
  finalScore?: number = null;
  actualScore?: number = null;
  gradeUnitId: number = null;
  gradeUnitDayId: number = null;

  constructor(configOverride?: Partial<IStudentAssignmentRequest>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}

export interface ICreateOrUpdateStudentAssignmentDTO {
  enrolledStudents?: IStudentDTO[] | null;
  tableData?: IStudentAssignmentsGrid[] | null;
  columnIndex: number;
  gradeType: GradingType;
  classroomId?: string;
}

export class CreateOrUpdateStudentAssignmentDTO
  implements ICreateOrUpdateStudentAssignmentDTO
{
  enrolledStudents: IStudentDTO[];
  tableData: IStudentAssignmentsGrid[];
  columnIndex: number = 0;
  gradeType: GradingType;
  classroomId: string = '';

  constructor(configOverride?: Partial<ICreateOrUpdateStudentAssignmentDTO>) {
    if (configOverride) {
      Object.assign(this, configOverride);
    }
  }
}
